import { LocalDate } from "@js-joda/core";
import type {
	CalendarType,
	CalendarViewType,
} from "./calendar/CalendarContext";

export const LinkGen = {
	Dashboard: () => "/",
	Onboard: () => "/dashboard/onboard",
	Login: (inviteToken?: string) =>
		`/auth${inviteToken ? `?inviteToken=${inviteToken}` : ""}`,
	Register: (inviteToken?: string) =>
		`/auth/register${inviteToken ? `?inviteToken=${inviteToken}` : ""}`,
	AcceptInvite: (token: string) => `/auth/invite/${token}`,
	VerificationResend: () => "/auth/verify",
	PasswordResetRequest: () => "/auth/forgot-password",
	PasswordResetConsume: (id: string, token: string) =>
		`/auth/forgot-password/${id}/${token}`,
	Calendar: (
		date?: LocalDate | string,
		type: CalendarType | string = "month",
		view: CalendarViewType | string = "calendar",
	) => {
		if (typeof date === "string") {
			return `/calendar/${date}/${type}/${view}`;
		}

		return `/calendar/${date ?? LocalDate.now()}/${type}/${view}`;
	},
	Settings: {
		Tab: (tabName: string) => `/settings/${tabName}`,
		Apps: () => "/settings/apps",
		TeamAccess: () => "/settings/team-access",
		Billing: () => "/settings/billing",
		User: () => "/settings/user",
		Notifications: () => "/settings/notifications",
		MarketingPlan: () => "/settings/marketing-plan",
		ICPs: () => "/settings/ideal-customer-profiles",
		CompanyInformation: () => "/settings/company-info",
		CanvaAudit: () => "/settings/canva-audit",
		XSpendAdmin: () => "/settings/x-spend",
		Video: () => "/settings/video",
		VideoAdmin: () => "/settings/video-admin",
	},
	Inbox: () => "/inbox",
	Media: () => "/media",
	MediaEdit: (id: number | string) => `/media/${id}`,
	Post: (id: number | string) => `/post/${id}`,
	PostCreate: () => "/post/create",
	ShortsCreate: () => "/shorts/new",
	ShortsQueue: () => "/shorts/queue",
	BlogPost: (id: number | string) => `/blog/${id}`,
	BlogPostCreate: () => "/blog/create",
	Report: () => "/report",
	Seo: () => "/seo",
	SeoAudit: () => "/seo-audit",
	PaymentSimulation: () => "/payment-simulation",
	MetaAds: {
		Index: () => "/meta-ads",
		CampaignCreate: () => "/meta-ads/campaigns/create",
		CampaignEdit: (id: number | string) => `/meta-ads/campaigns/edit/${id}`,
		DraftEdit: (id: number | string) => `/meta-ads/campaigns/drafts/${id}/edit`,
		Leads: () => "/meta-ads/leads",
		Audiences: () => "/meta-ads/audiences",
		Reporting: () => "/meta-ads/reporting",
		Reports: () => "/meta-ads/reports",
		CampaignTemplates: () => "/meta-ads/campaigns/templates",
		Approvals: () => "/meta-ads/approvals",
		ApprovalReview: (id: number | string) => `/meta-ads/approvals/${id}`,
		Team: () => "/meta-ads/team",
		AdminConnections: () => "/meta-ads/admin/connections",
		OAuthResult: () => "/meta-ads/oauth",
	},
	TikTokAds: {
		Index: () => "/tiktok-ads",
		CampaignCreate: () => "/tiktok-ads/campaigns/create",
		DraftEdit: (id: number | string) =>
			`/tiktok-ads/campaigns/drafts/${id}/edit`,
	},
	Google: {
		MyBusiness: {
			Tab: (tabName: string) => `/business-manager/${tabName}`,
			profile: () => "/business-manager/profile",
			reviews: () => "/business-manager/reviews",
		},
	},
};

export const QueryKeys = {
	Profile: () => ["PROFILE"],
	AppMetadata: () => ["APP", "METADATA"],
	AuthCheck: () => ["AUTH_CHECK"],
	Seo: {
		Audit: (companyId: number) => ["COMPANY", companyId, "SEO_AI_AUDIT"],
		AIVisibility: (companyId: number) => [
			"COMPANY",
			companyId,
			"SEO_AI_VISIBILITY",
		],
		Config: (companyId: number) => ["COMPANY", companyId, "SEO_CONFIG"],
		Locations: (companyId: number, query: string) => [
			"COMPANY",
			companyId,
			"SEO_LOCATIONS",
			query,
		],
		Metrics: (companyId: number) => ["COMPANY", companyId, "SEO_METRICS"],
	},
	Company: {
		All: () => ["COMPANY"],
		MarketingPlan: (companyId: number) => [
			"COMPANY",
			companyId,
			"MARKETING_PLAN",
		],
	},
	Locations: {
		Master: (companyId: number, googleLocationId?: string) =>
			googleLocationId
				? ["COMPANY", companyId, "LOCATIONS", "MASTER", googleLocationId]
				: ["COMPANY", companyId, "LOCATIONS", "MASTER"],
		Reviews: (companyId: number, googleLocationId?: string) =>
			googleLocationId
				? ["COMPANY", companyId, "LOCATIONS", "REVIEWS", googleLocationId]
				: ["COMPANY", companyId, "LOCATIONS", "REVIEWS"],
		ReviewReplyRules: (companyId: number) => [
			"COMPANY",
			companyId,
			"LOCATIONS",
			"REVIEW_REPLY_RULES",
		],
		ListingHealth: (companyId: number) => [
			"COMPANY",
			companyId,
			"LOCATIONS",
			"LISTING_HEALTH",
		],
		History: (companyId: number, googleLocationId?: string) =>
			googleLocationId
				? ["COMPANY", companyId, "LOCATIONS", "HISTORY", googleLocationId]
				: ["COMPANY", companyId, "LOCATIONS", "HISTORY"],
		CategoryMappings: (companyId: number, googleLocationId?: string) =>
			googleLocationId
				? [
						"COMPANY",
						companyId,
						"LOCATIONS",
						"CATEGORY_MAPPINGS",
						googleLocationId,
					]
				: ["COMPANY", companyId, "LOCATIONS", "CATEGORY_MAPPINGS"],
	},
	Posts: {
		All: (companyId: number) => ["COMPANY", companyId, "POSTS"],
		Latest: (companyId: number) => ["COMPANY", companyId, "POSTS", "LATEST"],
		Recent: (companyId: number) => ["COMPANY", companyId, "POSTS", "RECENT"],
		Single: (companyId: number, ID: number | null | undefined) => [
			"COMPANY",
			companyId,
			"POSTS",
			ID,
		],
		WithSiblings: (companyId: number, ID: number) => [
			"COMPANY",
			companyId,
			"POSTS",
			ID,
			"SIBLINGS",
		],
		Next: (companyId: number, ID: number) => [
			"COMPANY",
			companyId,
			"POSTS",
			ID,
			"NEXT",
		],
		List: (companyId: number) => ["COMPANY", companyId, "POSTS", "LIST"],
		ListRange: (companyId: number, from: LocalDate, to: LocalDate) => [
			"COMPANY",
			companyId,
			"POSTS",
			"LIST",
			from.toString(),
			to.toString(),
		],
		PublishingQueue: (companyId: number, platform: string) => [
			"COMPANY",
			companyId,
			"POSTS",
			"PUBLISHING_QUEUE",
			platform,
		],
	},
	Integrations: {
		Facebook: {
			Status: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"FACEBOOK",
				"STATUS",
			],
			Profiles: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"FACEBOOK",
				"PROFILES",
			],
		},
		Instagram: {
			Status: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"INSTAGRAM",
				"STATUS",
			],
			Profiles: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"INSTAGRAM",
				"PROFILES",
			],
		},
		ThreadsStatus: (companyId: number) => [
			"COMPANY",
			companyId,
			"INTEGRATIONS",
			"THREADS",
			"STATUS",
		],
		Linkedin: {
			Status: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"LINKEDIN",
				"STATUS",
			],
			Profiles: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"LINKEDIN",
				"PROFILES",
			],
		},
		Wordpress: {
			Status: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"WORDPRESS",
				"STATUS",
			],
		},
		Wix: {
			Status: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"WIX",
				"STATUS",
			],
		},
		MetaAds: {
			Status: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"STATUS",
			],
			AdAccounts: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"AD_ACCOUNTS",
			],
			AdAccountSingle: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"AD_ACCOUNT",
			],
			Overview: () => ["INTEGRATIONS", "META_ADS", "OVERVIEW"],
			AdminCheck: () => ["INTEGRATIONS", "META_ADS", "ADMIN_CHECK"],
			AdminConnections: () => ["INTEGRATIONS", "META_ADS", "ADMIN_CONNECTIONS"],
			Pixels: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"PIXELS",
			],
			CampaignForEdit: (companyId: number, campaignId: string) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"CAMPAIGN_FOR_EDIT",
				campaignId,
			],
			Roles: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"ROLES",
			],
			Drafts: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"DRAFTS",
			],
			Draft: (companyId: number, draftId: string) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"DRAFTS",
				draftId,
			],
			LeadForms: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"LEAD_FORMS",
			],
			MetaLeadForms: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"META_LEAD_FORMS",
			],
			Leads: (companyId: number, formId?: string) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"LEADS",
				formId,
			],
			Pages: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"PAGES",
			],
			PageSingle: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"PAGE",
			],
			MyRole: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"MY_ROLE",
			],
			DeliveryEstimate: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"DELIVERY_ESTIMATE",
			],
			Audiences: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"AUDIENCES",
			],
			ReportTemplates: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"REPORT_TEMPLATES",
			],
			ReportSchedules: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"REPORT_SCHEDULES",
			],
			ReportBranding: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"REPORT_BRANDING",
			],
			CampaignTemplates: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"CAMPAIGN_TEMPLATES",
			],
			Approvals: (companyId: number, status?: string) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"APPROVALS",
				status,
			],
			Approval: (companyId: number, id: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"APPROVALS",
				id,
			],
			ApprovalSettings: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"APPROVAL_SETTINGS",
			],
			GeoSearch: (companyId: number, query: string, type: string) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"GEO_SEARCH",
				query,
				type,
			],
			GeoTemplates: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"GEO_TEMPLATES",
			],
			GeoTemplate: (companyId: number, id: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"GEO_TEMPLATE",
				id,
			],
			PendingApprovalCount: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"META_ADS",
				"PENDING_APPROVAL_COUNT",
			],
			Reporting: {
				Dashboard: (companyId: number, from: string, to: string) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"META_ADS",
					"REPORTING",
					"DASHBOARD",
					from,
					to,
				],
				Campaigns: (companyId: number, from: string, to: string) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"META_ADS",
					"REPORTING",
					"CAMPAIGNS",
					from,
					to,
				],
				CampaignAdSets: (
					companyId: number,
					campaignId: string,
					from: string,
					to: string,
				) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"META_ADS",
					"REPORTING",
					"CAMPAIGNS",
					campaignId,
					"ADSETS",
					from,
					to,
				],
				AdSetAds: (
					companyId: number,
					adSetId: string,
					from: string,
					to: string,
				) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"META_ADS",
					"REPORTING",
					"ADSETS",
					adSetId,
					"ADS",
					from,
					to,
				],
				CrossClientOverview: (from: string, to: string) => [
					"INTEGRATIONS",
					"META_ADS",
					"REPORTING",
					"OVERVIEW",
					from,
					to,
				],
				Disapproved: (companyId: number) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"META_ADS",
					"REPORTING",
					"DISAPPROVED",
				],
			},
		},
		YouTube: {
			Status: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"YOUTUBE",
				"STATUS",
			],
			Channels: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"YOUTUBE",
				"CHANNELS",
			],
			Overview: () => ["INTEGRATIONS", "YOUTUBE", "OVERVIEW"],
		},
		TikTok: {
			Status: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"TIKTOK",
				"STATUS",
			],
		},
		X: {
			Status: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"X",
				"STATUS",
			],
			AdminCheck: () => ["INTEGRATIONS", "X", "ADMIN_CHECK"],
			AdminSpend: (year: number, month: number) => [
				"INTEGRATIONS",
				"X",
				"ADMIN_SPEND",
				year,
				month,
			],
		},
		TikTokAds: {
			Status: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"TIKTOK_ADS",
				"STATUS",
			],
			Advertisers: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"TIKTOK_ADS",
				"ADVERTISERS",
			],
			Report: (companyId: number, days: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"TIKTOK_ADS",
				"REPORT",
				days,
			],
			Drafts: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"TIKTOK_ADS",
				"DRAFTS",
			],
			Draft: (companyId: number, draftId: string) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"TIKTOK_ADS",
				"DRAFT",
				draftId,
			],
		},
		Canva: {
			Status: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"CANVA",
				"STATUS",
			],
			Designs: (
				companyId: number,
				assetFilter: string,
				continuation: string | null,
				search: string,
				sortBy: string,
				dateFilter: string,
				canvaFolderId: string,
			) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"CANVA",
				"DESIGNS",
				assetFilter,
				continuation ?? "START",
				search || "ALL",
				sortBy,
				dateFilter,
				canvaFolderId,
			],
			Import: (companyId: number, importId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"CANVA",
				"IMPORT",
				importId,
			],
			AuditEvents: (companyId: number, limit: number, offset: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"CANVA",
				"AUDIT_EVENTS",
				limit,
				offset,
			],
		},
		HeyGen: {
			Status: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"HEYGEN",
				"STATUS",
			],
			Videos: (
				companyId: number,
				token: string | null,
				title: string,
				folderId: string,
			) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"HEYGEN",
				"VIDEOS",
				token ?? "START",
				title || "ALL",
				folderId || "ALL_FOLDERS",
			],
			Folders: (companyId: number, folderIds: string[]) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"HEYGEN",
				"FOLDERS",
				...folderIds,
			],
			Import: (companyId: number, importId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"HEYGEN",
				"IMPORT",
				importId,
			],
		},
		Google: {
			Profiles: (companyId: number) => [
				"COMPANY",
				companyId,
				"INTEGRATIONS",
				"GOOGLE",
				"PROFILES",
			],
			SearchConsole: {
				WebsitesSingle: (companyId: number) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"SEARCH_CONSOLE",
					"WEBSITE",
				],
				Websites: (companyId: number) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"SEARCH_CONSOLE",
					"WEBSITES",
				],
				Status: (companyId: number) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"SEARCH_CONSOLE",
					"STATUS",
				],
			},
			MyBusiness: {
				Status: (companyId: number) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"MY_BUSINESS",
					"STATUS",
				],
				Accounts: (companyId: number) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"MY_BUSINESS",
					"ACCOUNTS",
				],
				AccountsSingle: (companyId: number) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"MY_BUSINESS",
					"ACCOUNTS",
					"SINGLE",
				],
				AccountsAsProfiles: (companyId: number) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"MY_BUSINESS",
					"ACCOUNTS_AS_PROFILES",
				],
				Locations: (companyId: number) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"MY_BUSINESS",
					"LOCATIONS",
				],
				SelectedLocation: (companyId: number) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"MY_BUSINESS",
					"SELECTED_LOCATION",
				],
				LocationsSingle: (companyId: number, locationId: string) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"MY_BUSINESS",
					"LOCATIONS",
					locationId,
				],
				LocationsSingleMedia: (companyId: number, locationId: string) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"MY_BUSINESS",
					"LOCATIONS",
					locationId,
					"MEDIA",
				],
				LocationsAvailableAttributes: (
					companyId: number,
					locationId: string,
				) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"MY_BUSINESS",
					"LOCATIONS",
					locationId,
					"AVAILABLE_ATTRIBUTES",
				],
				LocationsSingleAttributes: (companyId: number, locationId: string) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"MY_BUSINESS",
					"LOCATIONS",
					locationId,
					"ATTRIBUTES",
				],
				LocationsSingleReviews: (companyId: number, locationId: string) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"MY_BUSINESS",
					"LOCATIONS",
					locationId,
					"REVIEWS",
				],
				Categories: (companyId: number) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"MY_BUSINESS",
					"CATEGORIES",
				],
				BatchCategories: (companyId: number, categories: string[]) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"MY_BUSINESS",
					"BATCH_CATEGORIES",
					...categories,
				],
			},
			Analytics: {
				Status: (companyId: number) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"ANALYTICS",
					"STATUS",
				],
				Property: (companyId: number) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"ANALYTICS",
					"PROPERTY",
				],
				Traffic: (companyId: number) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"ANALYTICS",
					"TRAFFIC",
				],
			},
			Places: {
				Autocomplete: (companyId: number, input: string) => [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"GOOGLE",
					"PLACES",
					"AUTOCOMPLETE",
					input,
				],
			},
		},
	},
	Billing: {
		Status: (companyId: number) => ["COMPANY", companyId, "BILLING"],
		Entitlements: (companyId: number) => [
			"COMPANY",
			companyId,
			"BILLING",
			"ENTITLEMENTS",
		],
		Invoices: (companyId: number) => [
			"COMPANY",
			companyId,
			"BILLING",
			"INVOICES",
		],
		Prices: (companyId: number) => ["COMPANY", companyId, "PRICES"],
	},
	Files: {
		All: (companyID: number) => ["COMPANY", companyID, "FILES"],
		List: (companyID: number, query?: string) => {
			return [
				//
				"COMPANY",
				companyID,
				"FILES-IDS",
				...(query ? [query] : []),
			];
		},
		Single: (companyID: number, fileID: number | null | undefined) => [
			"COMPANY",
			companyID,
			"FILES",
			fileID,
		],
		Page: (
			companyID: number,
			folderId?: number | null,
			query?: string,
			source?: string,
		) => [
			"COMPANY",
			companyID,
			"FILES",
			"PAGE",
			// Distinguish All (undefined) from Unfiled (null) — both must not collapse to "ALL"
			folderId === undefined ? "ALL" : folderId === null ? "UNFILED" : folderId,
			...(source ? [source] : []),
			...(query ? [query] : []),
		],
		Folders: (companyID: number) => ["COMPANY", companyID, "FILES", "FOLDERS"],
	},
	Report: (companyId: number) => ["COMPANY", companyId, "REPORT"],
	ReportPostStatus: (companyId: number) => [
		"COMPANY",
		companyId,
		"REPORT-POST-STATUS",
	],
	ReportTopPosts: (companyId: number) => [
		"COMPANY",
		companyId,
		"REPORT-TOP-POSTS",
	],
	Onboarding: {
		Overview: (companyId: number) => [
			"COMPANY",
			companyId,
			"ONBOARDING",
			"OVERVIEW",
		],
	},
	Invitation: (token: string) => ["INVITATION", token],
	Inbox: {
		Conversations: (companyId: number) => [
			"COMPANY",
			companyId,
			"INBOX",
			"CONVERSATIONS",
		],
		Conversation: (companyId: number, conversationId: number) => [
			"COMPANY",
			companyId,
			"INBOX",
			"CONVERSATION",
			conversationId,
		],
		ConversationPrefix: (companyId: number) => [
			"COMPANY",
			companyId,
			"INBOX",
			"CONVERSATION",
		],
		UnreadCount: (companyId: number) => [
			"COMPANY",
			companyId,
			"INBOX",
			"UNREAD_COUNT",
		],
		SyncStatus: (companyId: number) => [
			"COMPANY",
			companyId,
			"INBOX",
			"SYNC_STATUS",
		],
	},
	Notifications: {
		List: (companyId: number) => ["COMPANY", companyId, "NOTIFICATIONS"],
		UnreadCount: (companyId: number) => [
			"COMPANY",
			companyId,
			"NOTIFICATIONS",
			"UNREAD_COUNT",
		],
		Preferences: (companyId: number) => [
			"COMPANY",
			companyId,
			"NOTIFICATIONS",
			"PREFERENCES",
		],
	},
	Videos: {
		All: (companyId: number) => ["COMPANY", companyId, "VIDEOS"],
		Availability: (companyId: number) => [
			"COMPANY",
			companyId,
			"VIDEOS",
			"AVAILABILITY",
		],
		Options: (companyId: number) => ["COMPANY", companyId, "VIDEOS", "OPTIONS"],
		Usage: (companyId: number) => ["COMPANY", companyId, "VIDEOS", "USAGE"],
		List: (companyId: number, postId: number | null) => [
			"COMPANY",
			companyId,
			"VIDEOS",
			"LIST",
			postId ?? "ALL",
		],
		Active: (companyId: number) => ["COMPANY", companyId, "VIDEOS", "ACTIVE"],
		AdminCheck: () => ["VIDEOS", "ADMIN_CHECK"],
		AdminOverview: () => ["VIDEOS", "ADMIN_OVERVIEW"],
	},
};

export const MutationKeys = {
	RequestSession: () => ["RequestSession"],
	ConsumeSession: () => ["ConsumeSession"],
};
